/**
 * Accept Invitation Page
 * Allows invited users to set their password and activate their account
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, Loader2, Eye, EyeOff, Lock, Building,
  Mail, User, ArrowRight
} from 'lucide-react';
import { validateInvitationToken, acceptInvitation, InvitationValidation } from '../services/invitationService';
import { getErrorMessage } from '../services/apiService';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';

const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [invitation, setInvitation] = useState<InvitationValidation | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided. Please use the link from your invitation email.');
      setValidating(false);
      return;
    }

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      setValidating(true);
      const result = await validateInvitationToken(token);
      if (result.isValid) {
        setInvitation(result);
      } else {
        setError(result.message || 'This invitation is no longer valid.');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Validate password strength (basic check - server will do full validation)
    if (password.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }

    setSubmitting(true);

    try {
      await acceptInvitation(token, {
        password,
        confirmPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white/60">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-green-500/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Account Created!</h1>
          <p className="text-white/70 mb-8">
            Your account has been successfully created. You can now log in with your email and password.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-3 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white font-medium transition-all shadow-xl shadow-red-500/30 flex items-center justify-center space-x-2 mx-auto"
          >
            <span>Go to Login</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Error state (invalid token)
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-red-500/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Invalid Invitation</h1>
          <p className="text-white/70 mb-8">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-all"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Form state (valid token)
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Welcome to AutoAccess</h1>
            <p className="text-white/60">Complete your account setup</p>
          </div>

          {/* Invitation Details Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 mb-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white/60 text-sm">You've been invited to join</p>
                <h2 className="font-semibold text-lg">{invitation?.organizationName}</h2>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-white/10">
              <div className="flex items-center text-white/70">
                <User className="w-4 h-4 mr-3" />
                <span>{invitation?.firstName} {invitation?.lastName}</span>
              </div>
              <div className="flex items-center text-white/70">
                <Mail className="w-4 h-4 mr-3" />
                <span>{invitation?.email}</span>
              </div>
            </div>
          </div>

          {/* Password Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Set Your Password
            </h3>

            {error && (
              <div className="mb-4 p-4 rounded-xl border flex items-start space-x-3"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-200">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border bg-black/20 text-white pr-12"
                    style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5 text-white/60" /> : <Eye className="w-5 h-5 text-white/60" />}
                  </button>
                </div>
                {password && <PasswordStrengthIndicator password={password} />}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border bg-black/20 text-white pr-12"
                    style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5 text-white/60" /> : <Eye className="w-5 h-5 text-white/60" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm text-blue-200">
                <p className="font-medium mb-2">Password Requirements:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200/80">
                  <li>At least 12 characters</li>
                  <li>2 uppercase letters</li>
                  <li>2 lowercase letters</li>
                  <li>2 numbers</li>
                  <li>2 special characters (!@#$%^&*)</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={submitting || !password || !confirmPassword || password !== confirmPassword}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white font-medium transition-all shadow-xl shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Creating Account...</>
                ) : (
                  'Create My Account'
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-white/40 text-sm mt-6">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-white/70 hover:text-white underline"
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
